import { useState, useRef, useEffect } from 'react';
import { api } from '../api.js';

export default function ImportStudentsPage() {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [undoExpiryTime, setUndoExpiryTime] = useState(null);
  const fileInputRef = useRef(null);

  // Periodically check expiry to disable button when it expires
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!undoExpiryTime) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [undoExpiryTime]);

  // Restore undo expiry from sessionStorage on mount (survives page navigation)
  useEffect(() => {
    const saved = sessionStorage.getItem('import_undo_expiry');
    if (saved) setUndoExpiryTime(parseInt(saved, 10));
  }, []);

  function handleDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function handleDragLeave() {
    setDragging(false);
  }

  async function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) await processFile(droppedFile);
  }

 function handleFileSelect(e) {
    const selectedFile = e.target.files[0];
    if (selectedFile) processFile(selectedFile);
  }

  async function processFile(selectedFile) {
    setFile(selectedFile);
    setLoading(true);
    setError(null);
    setResult(null);
    setUndoExpiryTime(null);

    try {
      let response;
      if (selectedFile.name.endsWith('.csv')) {
        const text = await selectedFile.text();
        response = await api.importStudentsFromCSV(selectedFile.name, text);
      } else if (selectedFile.name.match(/\.xlsx?$/)) {
        const arrayBuffer = await selectedFile.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        response = await api.importStudentsFromXLSX(selectedFile.name, base64);
      } else {
        setError('Unsupported file format. Please upload .csv or .xlsx files.');
        setLoading(false);
        return;
      }

      setResult(response);
      if (response.undoToken) {
        const expiry = response.undoToken.expiry;
        setUndoExpiryTime(expiry);
        sessionStorage.setItem('import_undo_expiry', String(expiry));
      }
    } catch (err) {
      setError(err.message || 'Failed to import students');
    } finally {
      setLoading(false);
    }
  }

  async function handleUndo() {
    setLoading(true);
    try {
      const response = await api.undoImportStudents({ expiry: undoExpiryTime });
      setResult(response);
      setUndoExpiryTime(null);
      sessionStorage.removeItem('import_undo_expiry');
    } catch (err) {
      setError(err.message || 'Failed to undo import');
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadErrors(format) {
    try {
      const file = result.errorLogFile.split('/').pop();
      const data = await api.downloadImportErrors(file, format);
      api.saveBase64File(data);
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
  }

  function reset() {
    setFile(null);
    setResult(null);
    setError(null);
    setUndoExpiryTime(null);
    sessionStorage.removeItem('import_undo_expiry');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const requiredColumns = [
    'Full Name',
    'Optional ID',
    'Qualification Name',
    'Activity Start Date',
    'Activity End Date',
    'Unit of Competency Code',
    'Commencement Date',
    'Expected Completion Date'
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>Import Students</h2>
      <p style={{ color: '#5b6776', fontSize: 14, marginBottom: 24 }}>
        Upload a CSV or Excel file with student qualifications and unit data. Each row represents one unit within a qualification.
      </p>

      {/* Upload Area */}
      <div className="card" style={{ padding: 32, textAlign: 'center' }}>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? 'var(--brand)' : '#d1d5db'}`,
            borderRadius: 8,
            padding: '40px 20px',
            cursor: dragging ? 'grabbing' : 'pointer',
            background: dragging ? '#f0f7ff' : '#fafbfc',
            transition: 'all 0.15s',
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 15 }}>
            {dragging ? 'Drop your file here' : 'Drag & drop a CSV or Excel file here'}
          </p>
          <p style={{ margin: 0, color: '#95a5a6', fontSize: 13 }}>or click to browse • .csv, .xls, .xlsx supported</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {file && (
          <div style={{ marginTop: 16 }}>
            <p style={{ color: '#2ecc71', fontWeight: 500, margin: '0 0 4px' }}>✓ Selected: {file.name}</p>
            {loading && <p style={{ color: '#3498db', fontSize: 13, margin: 0 }}>⏳ Processing...</p>}
          </div>
        )}
      </div>

      {/* Required Columns Info */}
      <div className="card" style={{ marginTop: 24, padding: 20 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Required CSV/Excel Columns</h3>
        <ul style={{ margin: '12px 0 0', paddingLeft: 20, color: '#5b6776', lineHeight: 1.8 }}>
          {requiredColumns.map(col => (
            <li key={col}><strong>{col}</strong></li>
          ))}
        </ul>
        <p style={{ fontSize: 13, color: '#95a5a6', margin: '12px 0 0' }}>
          Each row = one unit. A student can have multiple rows for the same qualification (multiple units) or different qualifications.
        </p>
      </div>

      {/* Results */}
      {result && (
        <div className="card" style={{ marginTop: 24, padding: 24, background: '#f0fdf4', border: '1px solid #86efac' }}>
          <h3 style={{ margin: '0 0 12px', color: '#16a34a', fontSize: 16 }}>✓ Import Complete</h3>
          <p style={{ margin: '0 0 8px', color: '#5b6776' }}>
            Added: <strong>{result.added}</strong> students | Updated: <strong>{result.updated}</strong> students
            {result.mergedQualifications > 0 && <> | Merged qualifications: <strong>{result.mergedQualifications}</strong></>}
          </p>
         {undoExpiryTime && (
            <div style={{ marginTop: 12, padding: 12, background: '#eff6ff', borderRadius: 6 }}>
              <p style={{ margin: '0 0 8px', color: '#1e4a7a', fontWeight: 500, fontSize: 13 }}>
                ⏱ Undo available at {new Date(undoExpiryTime).toLocaleTimeString()}
              </p>
              <button
                onClick={handleUndo}
                disabled={!undoExpiryTime || now >= undoExpiryTime}
                style={{
                  background: (undoExpiryTime && now < undoExpiryTime) ? '#3b82f6' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  padding: '6px 16px',
                  borderRadius: 4,
                  cursor: (undoExpiryTime && now < undoExpiryTime) ? 'pointer' : 'not-allowed',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Undo Import
              </button>
            </div>
          )}
          {result.skippedDuplicates > 0 && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef3c7', borderRadius: 6 }}>
              <p style={{ margin: '0 0 4px', color: '#92400e', fontWeight: 500, fontSize: 13 }}>⚠ Duplicates Skipped: {result.skippedDuplicates}</p>
              {result.mergedQualifications > 0 && (
                <p style={{ margin: '4px 0 0', color: '#92400e', fontSize: 13 }}>
                  • {result.mergedQualifications} qualification(s) merged — existing units updated by code, new units added
                </p>
              )}
            </div>
          )}
          {result.errors && result.errors.length > 0 && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef3c7', borderRadius: 6 }}>
              <p style={{ margin: '0 0 4px', color: '#92400e', fontWeight: 500, fontSize: 13 }}>⚠ Errors &amp; Warnings:</p>
              {result.errors.map((err, i) => (
                <p key={i} style={{ margin: 0, color: '#92400e', fontSize: 13 }}>
                  <span style={{ fontWeight: 700, color: err.status === 'failed' ? '#dc2626' : '#92400e' }}>
                    [{err.status === 'failed' ? 'ERROR' : 'WARNING'}]
                  </span>{' '}
                  Row {err.row + 1}: {(err.reasons || [err.reason]).join('; ')}
                </p>
              ))}
              {result.errorLogFile && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button onClick={() => handleDownloadErrors('json')} style={{
                    background: 'transparent', color: '#1e4a7a', border: '1px solid #93c5fd',
                    padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500
                  }}>Download errors (JSON)</button>
                  <button onClick={() => handleDownloadErrors('xlsx')} style={{
                    background: 'transparent', color: '#1e4a7a', border: '1px solid #93c5fd',
                    padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500
                  }}>Download errors (Excel)</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="card" style={{ marginTop: 24, padding: 24, background: '#fef2f2', border: '1px solid #fecaca' }}>
          <h3 style={{ margin: '0 0 8px', color: '#dc2626', fontSize: 16 }}>✗ Import Failed</h3>
          <p style={{ margin: 0, color: '#5b6776', fontSize: 14 }}>{error}</p>
        </div>
      )}

      {/* Reset Button */}
      {result && (
        <button onClick={reset} style={{ marginTop: 16 }}>Import Another File</button>
      )}
    </div>
  );
}
